/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { AnnotationTypes } from '@atlaskit/adf-schema';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box, Text, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';

import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments';
import type {
	CommentLocation,
	CommentInlineResolveProperties,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	CommentType,
	CommentActionType,
	type CommentData,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { fg } from '@confluence/feature-gating';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	useCommentsPanel,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils';
import { useGetCommentStatus } from '@confluence/comments-util/entry-points/commentStatus';

import {
	type CommentThreadMouseEnterParams,
	handleCommentThreadMouseEnter,
	handleCommentThreadMouseLeave,
	handleCommentThreadSelection,
} from '../helper/commentThreadHelper';

import { CommentReplies } from './CommentReplies';
import { Comment } from './Comment';

export type CommentThreadProps = {
	parentComment: CommentData & {
		isFirstPageComment?: boolean;
	};
	supportedTopLevelActions: CommentAction[];
	eventEmitter: AnnotationUpdateEmitter;
	pageMode: PageMode;
	flags?: FlagsStateContainer;
	shouldRenderCommentThreadSeperator?: boolean;
	shouldRenderCommentTypeSeparator?: boolean;
};

const dividerStyle = xcss({
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	position: 'relative',
	top: 'space.200',
});

const commentThreadBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	margin: '0',
	display: 'flex',
	flexDirection: 'column',
});

const newCommentThreadBoxStyles = xcss({
	cursor: 'pointer',
});

const commentTypeSeperatorStyle = xcss({
	display: 'flex',
	gap: 'space.050',
	paddingTop: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
});

const commentBodyBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
});

const isCurrentSelectedCommentStyles = xcss({
	background: token('color.background.selected'),
});

const isCurrentUserSelectedCommentStyles = xcss({
	background: token('color.background.input.hovered'),
});

const isThreadHoveredStyles = xcss({
	background: token('elevation.surface.hovered'),
});

const isThreadPressedStyles = xcss({
	background: token('color.background.input.hovered'),
});

const i18n = defineMessages({
	commentTypeSeperatorText: {
		id: 'comments-panel.comment.thread.type-seperator',
		defaultMessage: 'General comments',
		description: 'Text for the seperator of inline and general comment section',
	},
	contentAriaLabelWithName: {
		id: 'comments-panel.comment.thread.content.aria-label.with-name',
		defaultMessage: '{name}’s comment thread, {timestamp}',
		description:
			'Aria-label for content associated with the comment thread, with the first comment author name and timestamp',
	},
});

// This component contains parent comment and its replies
export const CommentThread = ({
	parentComment,
	supportedTopLevelActions,
	eventEmitter,
	pageMode,
	flags,
	shouldRenderCommentThreadSeperator,
	shouldRenderCommentTypeSeparator,
}: CommentThreadProps) => {
	const intl = useIntl();
	const { formatMessage } = intl;
	const pageClass = usePageClass();

	const [
		{ currentlySelectedThreadKey, commentsMarkedAsRead, currentView },
		{ setCurrentlySelectedThreadKey, setCommentsInThreadToBeMarkedAsRead },
	] = useCommentsPanel();
	const { pageInfo } = usePageInfo({
		fetchPolicy: fg('confluence-comment-pageInfo-use-cache-first')
			? 'cache-first'
			: 'cache-and-network',
	});
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const threadKey =
		parentComment.type === CommentType.INLINE
			? ((parentComment.location as CommentLocation).inlineMarkerRef ?? '')
			: parentComment.id;

	const selectedAnnotation = {
		id: threadKey || '',
		type: AnnotationTypes.INLINE_COMMENT,
	};

	// State
	const [isThreadHovered, setIsThreadHovered] = useState<boolean>(false);
	const [isThreadPressed, setIsThreadPressed] = useState<boolean>(false);
	const [hoveredCommentId, setHoveredCommentId] = useState<string | undefined>(undefined);
	const [isAnnotatedTextPressed, setAnnotatedTextPressed] = useState(false);
	const [commentIsFocused, setCommentIsFocused] = useState<boolean>(false);

	// Hook dependent constants
	const isCurrentSelectedComment = currentlySelectedThreadKey === threadKey;

	const { editCommentId: editCommentQueryId } = useInlineCommentQueryParams();

	const { canAddComments } = useAddCommentPermissionCheck(pageInfo?.id ?? '');
	const [{ unreadCommentIDSet }, { updateUnreadCommentsListState }] = useUnreadComments();

	const { commentService } = useCommentService();

	const handleSelectThread = useCallback(
		(selectedThreadKey?: string) => {
			// Only open comments are visible in content types
			if (currentView !== ViewValues.RESOLVED) {
				commentService.handleSelectThread(selectedThreadKey);
			}
		},
		[commentService, currentView],
	);

	const handleCommentThreadHoverProps: CommentThreadMouseEnterParams = useMemo(
		() => ({
			threadKey,
			setIsThreadHovered,
			setHoveredCommentId,
			pageMode,
			eventEmitter,
		}),
		[threadKey, setIsThreadHovered, setHoveredCommentId, pageMode, eventEmitter],
	);

	const handleMouseEnter = useCallback(
		(id?: string) => {
			handleCommentThreadMouseEnter({
				...handleCommentThreadHoverProps,
				id,
			});
		},
		[handleCommentThreadHoverProps],
	);

	const handleMouseLeave = useCallback(() => {
		handleCommentThreadMouseLeave(handleCommentThreadHoverProps);
	}, [handleCommentThreadHoverProps]);

	const handleFocus = (event: React.FocusEvent) => {
		event.stopPropagation();
		setCommentIsFocused(true);
	};

	const handleBlur = (event: React.FocusEvent) => {
		if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
			setCommentIsFocused(false);
		}
	};

	const getBackgroundStyle = () => {
		if (isCurrentSelectedComment && !fg('confluence_frontend_comments_panel_v2')) {
			return isCurrentSelectedCommentStyles;
		}
		if (isCurrentSelectedComment && fg('confluence_frontend_comments_panel_v2')) {
			return isCurrentUserSelectedCommentStyles;
		}
		if (isThreadHovered && !fg('confluence_frontend_comments_panel_v2')) {
			return isThreadHoveredStyles;
		}
		if (isThreadPressed && !isAnnotatedTextPressed && fg('confluence_frontend_comments_panel_v2')) {
			return isThreadPressedStyles;
		}
		return undefined;
	};

	const userActionBackgroundStyles = getBackgroundStyle();

	const unreadParentCommentAlreadyMarkedAsReadRef = useRef(false);

	if (
		isCurrentSelectedComment &&
		!unreadParentCommentAlreadyMarkedAsReadRef.current &&
		parentComment.isUnread &&
		!fg('confluence_frontend_unread_comments_fixes')
	) {
		setCommentsInThreadToBeMarkedAsRead({
			threadKey,
			commentIds: [parentComment.id],
			commentType: parentComment.type,
		});
		unreadParentCommentAlreadyMarkedAsReadRef.current = true;
		updateUnreadCommentsListState((prev) => {
			return prev.filter((unreadComment) => {
				if (unreadComment.id === parentComment.id) {
					return false;
				}
				return true;
			});
		});
	}

	const isCommentRemovedByAnotherUser = parentComment?.wasRemovedByAnotherUser;
	const isResolvedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD;

	// if comment thread is resolved, mark it as read exactly once
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (isResolvedByAnotherUser) {
			const unreadCommentIds: string[] = [];

			if (parentComment.isUnread) {
				unreadCommentIds.push(parentComment.id);
			}

			const unreadReplies = parentComment.replies.filter((reply) => reply.isUnread);
			if (unreadReplies.length > 0) {
				unreadCommentIds.push(...unreadReplies.map((reply) => reply.id));
			}

			if (unreadCommentIds.length > 0) {
				setCommentsInThreadToBeMarkedAsRead({
					threadKey,
					commentIds: unreadCommentIds,
					commentType: parentComment.type,
				});
				updateUnreadCommentsListState((prev) => {
					const unreadCommentIdsSet = new Set(unreadCommentIds);
					return prev.filter((unreadComment) => !unreadCommentIdsSet.has(unreadComment.id));
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleCommentThreadMouseDown = () => {
		if (parentComment.type === CommentType.GENERAL) {
			return;
		}
		const isActiveInlineComment =
			parentComment.type === CommentType.INLINE &&
			!parentComment.wasRemovedByAnotherUser &&
			'inlineResolveProperties' in parentComment.location &&
			parentComment.location.inlineResolveProperties?.resolved === false;

		if (isActiveInlineComment && !isCurrentSelectedComment) {
			selectInlineCommentCompoundExperience.selectAnnotation.start({
				pageClass,
				annotationId: (parentComment.location as CommentLocation).inlineMarkerRef!,
				entryPoint: 'sidebar',
			});
		}

		setIsThreadPressed(true);
		setCurrentlySelectedThreadKey(threadKey);
	};

	const canAddCommentsToCommentThread = canAddComments && currentView !== ViewValues.RESOLVED;

	const handleCommentThreadClick = (event: React.MouseEvent) => {
		handleCommentThreadSelection({
			parentComment,
			pageMode,
			scrollCommentsPanelToThread,
			setCurrentlySelectedThreadKey,
			eventEmitter,
			event,
			handleSelectThread,
		});
	};

	const commentStatus = useGetCommentStatus({
		resolveProperties:
			(parentComment.location as { inlineResolveProperties?: CommentInlineResolveProperties })
				?.inlineResolveProperties ||
			(parentComment.location as { commentResolveProperties?: CommentInlineResolveProperties })
				?.commentResolveProperties,
	});

	const timestamp = formatTimestampForAriaLabel({
		intl,
		dateString: parentComment.createdAtNonLocalized,
		commentStatus,
		resolveProperties:
			(parentComment.location as { inlineResolveProperties?: CommentInlineResolveProperties })
				?.inlineResolveProperties ||
			(parentComment.location as { commentResolveProperties?: CommentInlineResolveProperties })
				?.commentResolveProperties,
	});

	return (
		<>
			{shouldRenderCommentTypeSeparator && fg('confluence_frontend_content_wrapper') && (
				<Flex
					xcss={commentTypeSeperatorStyle}
					as="li"
					testId="comment-section-separator"
					alignItems="center"
				>
					<PageIcon
						testId="general-comment-page-icon"
						color={token('color.icon.subtlest')}
						label={formatMessage(i18n.commentTypeSeperatorText)}
					/>
					<Text color="color.text.subtlest" weight="medium">
						{formatMessage(i18n.commentTypeSeperatorText)}
					</Text>
				</Flex>
			)}
			{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-noninteractive-element-interactions, @atlassian/a11y/click-events-have-key-events */}
			<Box
				// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
				{...(fg('confluence_frontend_comments_a11y_kb')
					? {
							role: 'button',
							tabIndex: 0,
							'aria-label': formatMessage(i18n.contentAriaLabelWithName, {
								name: parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
								timestamp,
							}),
						}
					: {})}
				id={`comment-thread-${threadKey}-container`}
				testId={`comment-thread-${threadKey}-container`}
				xcss={[
					commentThreadBoxStyles,
					userActionBackgroundStyles,
					fg('confluence_frontend_comments_panel_v2') && newCommentThreadBoxStyles,
				]}
				data-selected-comment-thread={
					isCurrentSelectedComment && (parentComment.location as CommentLocation)?.inlineMarkerRef
						? (parentComment.location as CommentLocation).inlineMarkerRef
						: undefined
				}
				as="li"
				onClick={handleCommentThreadClick}
				onMouseDown={handleCommentThreadMouseDown}
				onMouseUp={() => setIsThreadPressed(false)}
				onFocus={handleFocus}
				onBlur={handleBlur}
			>
				<Box
					testId={`comment-thread-${parentComment.id}-parent`}
					xcss={commentBodyBoxStyles}
					onMouseEnter={() =>
						fg('confluence_frontend_comments_panel_v2')
							? handleMouseEnter(parentComment.id)
							: undefined
					}
					onMouseLeave={fg('confluence_frontend_comments_panel_v2') ? handleMouseLeave : undefined}
				>
					<Comment
						comment={parentComment}
						supportedTopLevelActions={supportedTopLevelActions}
						pageInfo={pageInfo}
						pageMode={pageMode}
						isUnread={
							fg('confluence_frontend_unread_comments_fixes')
								? unreadCommentIDSet.has(parentComment.id)
								: !commentsMarkedAsRead[parentComment.type][threadKey]?.has(parentComment.id) &&
									parentComment.isUnread
						}
						isHovered={
							hoveredCommentId === parentComment.id ||
							(commentIsFocused && fg('confluence_comments_hover_state_on_focused_comment'))
						}
						threadKey={threadKey}
						flags={flags}
						isAnnotatedTextPressed={isAnnotatedTextPressed}
						setAnnotatedTextPressed={setAnnotatedTextPressed}
						hideBranchingStyle={!parentComment.isOpen && parentComment.replies.length === 0}
						canAddComments={canAddCommentsToCommentThread}
						isThreadHovered={isThreadHovered}
					/>
				</Box>
				<CommentReplies
					parentComment={parentComment}
					pageInfo={pageInfo}
					isCurrentSelectedComment={isCurrentSelectedComment}
					hoveredCommentId={hoveredCommentId ?? ''}
					selectedAnnotation={selectedAnnotation}
					editCommentQueryId={editCommentQueryId ?? ''}
					pageMode={pageMode}
					parentCommentId={parentComment.id}
					handleMouseEnter={handleMouseEnter}
					handleMouseLeave={handleMouseLeave}
					canAddComments={canAddCommentsToCommentThread}
					eventEmitter={eventEmitter}
				/>
				{/* per new design, we don't need to show the divider, clean up later */}
				{shouldRenderCommentThreadSeperator &&
					!fg('confluence-frontend-comments-panel') &&
					!fg('confluence_frontend_comments_panel_v2') && (
						<Box xcss={dividerStyle} testId={`comment-thread-${parentComment.id}-bottom-divider`} />
					)}
			</Box>
		</>
	);
};
